import { useEffect } from "react";
import { Box, Grid, Typography, Card, CardContent, Divider, Avatar, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { NewsStore } from "../../store/newsStore";
import { BASE_URL } from "../../api/api";

export default function AboutUs() {
  const { fetchNews, news } = NewsStore();
  const navigate = useNavigate();
  const noImage = "https://upload.wikimedia.org/wikipedia/commons/d/d1/Image_not_available.png";

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!news || news.length === 0) {
      fetchNews(1, 20);
    }
  }, [fetchNews, news]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, mt: { xs: 20, sm: 22, md: 24 } }}>
      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 3,
              bgcolor: "background.paper",
              color: "text.primary"
            }}>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, color: "#ff184e", fontSize: { xs: "28px", md: "34px" } }}>
              About Us
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, fontSize: { xs: "20px", md: "24px" } }}>
              JanPath News
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 2 }}>
              JanPath News is an independent digital news platform focused on delivering reliable, timely, and relevant news from Chhattisgarh and Central India. Our mission is to bring the voices, issues, and developments of the region closer to readers through clear, factual, and accessible reporting.
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 4 }}>
              We cover a wide range of topics including regional news, politics, public policy, education, employment, civic issues, and social developments that directly impact people across Chhattisgarh and neighboring states.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              Our Vision
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 4 }}>
              Our vision is to build a trusted digital news platform that strengthens public awareness and promotes informed discussions within communities. JanPath News aims to highlight stories that matter at the local, district, and state level, ensuring that important developments reach readers quickly and accurately.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              What We Cover
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 1.5 }}>
              JanPath News focuses on delivering updates and insights across several areas:
            </Typography>
            <Box component="ul" sx={{ pl: { xs: 2.5, md: 4 }, mb: 2, listStyleType: 'disc', '& li': { fontSize: { xs: '16px', md: '18px' }, lineHeight: 1.8, mb: 1 }, '& li::marker': { color: '#ff184e' } }}>
              <li>Local and district news across Chhattisgarh</li>
              <li>State politics and governance</li>
              <li>Public policy and government schemes</li>
              <li>Education and employment updates</li>
              <li>Civic issues and community stories</li>
              <li>Social, cultural, and regional developments</li>
            </Box>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 4 }}>
              Our editorial approach prioritizes clarity, accuracy, and relevance so readers can stay informed about the issues shaping their communities.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              Our Commitment
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 1.5 }}>
              At JanPath News, we believe that access to information is essential for a strong and informed society. We are committed to:
            </Typography>
            <Box component="ul" sx={{ pl: { xs: 2.5, md: 4 }, mb: 4, listStyleType: 'disc', '& li': { fontSize: { xs: '16px', md: '18px' }, lineHeight: 1.8, mb: 1 }, '& li::marker': { color: '#ff184e' } }}>
              <li>Publishing factual and verified information</li>
              <li>Presenting news in a clear and unbiased manner</li>
              <li>Covering grassroots issues that often receive limited attention</li>
              <li>Maintaining responsible and ethical digital journalism standards</li>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              Digital-First Approach
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 4 }}>
              As a digital-first news platform, JanPath News focuses on delivering updates through modern online channels, making news easily accessible to readers across devices and locations.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              Looking Ahead
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 4 }}>
              JanPath News is continuously evolving as we expand our coverage, improve our reporting processes, and connect with readers across the region. Our goal is to become a dependable source for regional news and information in Chhattisgarh.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              Our Network & Regional Coverage
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 2 }}>
              JanPath News is building a growing network of contributors, reporters, and local information sources across Chhattisgarh, enabling us to cover developments from both urban centers and rural communities. Our goal is to ensure that important local stories from every district reach a wider audience.
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 4 }}>
              We aim to maintain coverage across all districts of the state through our developing network of local correspondents and authorized partners.
            </Typography>

            <Box sx={{ bgcolor: "rgba(255, 24, 78, 0.05)", p: {xs: 2, md: 3}, borderRadius: 2, mb: 4, border: "1px solid rgba(255, 24, 78, 0.1)" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                District Coverage in Chhattisgarh
              </Typography>
              <Typography sx={{ fontSize: "16px", lineHeight: 1.8, mb: 3, color: "text.secondary", textAlign: "center" }}>
                Our coverage includes news and updates from districts across the state, including:
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", color: "#ff184e", mb: 1, borderBottom: "1px dashed #ff184e", pb: 0.5 }}>Bastar Division</Typography>
                  <Box component="ul" sx={{ pl: { xs: 2.5, md: 3 }, m: 0, listStyleType: 'disc', '& li': { fontSize: { xs: '14px', md: '15px' }, mb: 0.5 }, '& li::marker': { color: '#ff184e' } }}>
                    <li>Bastar</li>
                    <li>Dantewada</li>
                    <li>Bijapur</li>
                    <li>Sukma</li>
                    <li>Kondagaon</li>
                    <li>Narayanpur</li>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", color: "#ff184e", mb: 1, borderBottom: "1px dashed #ff184e", pb: 0.5 }}>Durg Division</Typography>
                  <Box component="ul" sx={{ pl: { xs: 2.5, md: 3 }, m: 0, listStyleType: 'disc', '& li': { fontSize: { xs: '14px', md: '15px' }, mb: 0.5 }, '& li::marker': { color: '#ff184e' } }}>
                    <li>Durg</li>
                    <li>Balod</li>
                    <li>Bemetara</li>
                    <li>Rajnandgaon</li>
                    <li>Kabirdham (Kawardha)</li>
                    <li>Mohla–Manpur–Ambagarh Chowki</li>
                    <li>Khairagarh–Chhuikhadan–Gandai</li>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", color: "#ff184e", mb: 1, borderBottom: "1px dashed #ff184e", pb: 0.5 }}>Raipur Division</Typography>
                  <Box component="ul" sx={{ pl: { xs: 2.5, md: 3 }, m: 0, listStyleType: 'disc', '& li': { fontSize: { xs: '14px', md: '15px' }, mb: 0.5 }, '& li::marker': { color: '#ff184e' } }}>
                    <li>Raipur</li>
                    <li>Baloda Bazar–Bhatapara</li>
                    <li>Gariaband</li>
                    <li>Mahasamund</li>
                    <li>Dhamtari</li>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", color: "#ff184e", mb: 1, borderBottom: "1px dashed #ff184e", pb: 0.5 }}>Bilaspur Division</Typography>
                  <Box component="ul" sx={{ pl: { xs: 2.5, md: 3 }, m: 0, listStyleType: 'disc', '& li': { fontSize: { xs: '14px', md: '15px' }, mb: 0.5 }, '& li::marker': { color: '#ff184e' } }}>
                    <li>Bilaspur</li>
                    <li>Mungeli</li>
                    <li>Korba</li>
                    <li>Janjgir–Champa</li>
                    <li>Raigarh</li>
                    <li>Gaurela–Pendra–Marwahi</li>
                    <li>Sakti</li>
                    <li>Sarangarh–Bilaigarh</li>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", color: "#ff184e", mb: 1, borderBottom: "1px dashed #ff184e", pb: 0.5 }}>Surguja Division</Typography>
                  <Box component="ul" sx={{ pl: { xs: 2.5, md: 3 }, m: 0, listStyleType: 'disc', '& li': { fontSize: { xs: '14px', md: '15px' }, mb: 0.5 }, '& li::marker': { color: '#ff184e' } }}>
                    <li>Surguja</li>
                    <li>Surajpur</li>
                    <li>Balrampur–Ramanujganj</li>
                    <li>Koriya</li>
                    <li>Manendragarh–Chirmiri–Bharatpur</li>
                    <li>Jashpur</li>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5, color: "#ff184e" }}>
              Authorized Partners & Local Contributors
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, mb: 2 }}>
              JanPath News welcomes collaboration with local journalists, independent reporters, and community contributors who help us bring verified local updates from across the state. Where available, district-level updates may be supported by authorized partners, local contributors, and verified information sources working with the JanPath News editorial team.
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8 }}>
              As our network expands, we continue to strengthen our presence across districts to ensure timely and accurate regional coverage.
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: { xs: "static", md: "sticky" }, top: 120, mt: { xs: 4, md: 0 } }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, borderBottom: "2px solid #ff184e", display: "inline-block" }}>
              Latest News
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {news?.slice(0, 5).map((item) => (
                <Card
                  key={item.id}
                  sx={{
                    display: "flex",
                    cursor: "pointer",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    transition: "transform 0.3s, box-shadow 0.3s",
                    "&:hover": {
                      boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
                      transform: "translateY(-3px)"
                    }
                  }}
                  onClick={() => navigate(`/${item.slug}`)}
                >
                  <CardContent sx={{ flex: 1, p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {item.title}
                    </Typography>
                  </CardContent>
                  <Box sx={{ width: 80, height: 80, p: 1 }}>
                    <Avatar
                      src={item.image ? `${BASE_URL}${item.image}` : noImage}
                      variant="rounded"
                      sx={{ width: "100%", height: "100%", borderRadius: 1 }}
                    />
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
